const javascriptCheatSheet = {
  id: "javascript",
  title: "JavaScript Cheatsheet",
  level: "Beginner to Advanced",
  description:
    "A comprehensive guide to JavaScript fundamentals, ES6+ features, and modern development practices",
  language: "javascript",
  category: "Web Development",
  sections: [
    {
      title: "Variables & Data Types",
      icon: "📦",
      items: [
        {
          title: "Variable Declaration & Scope",
          content: "Different ways to declare variables and their scope rules",
          code: `// var - function scoped, hoisted
var x = 10;
function example() {
  var y = 20; // only accessible inside function
  if (true) {
    var z = 30; // accessible in entire function
  }
  console.log(z); // works: z is hoisted
}

// let - block scoped, not hoisted
let a = 1;
function example2() {
  let b = 2;
  if (true) {
    let c = 3; // only accessible in this block
  }
  // console.log(c); // ReferenceError
}

// const - block scoped, must be initialized
const PI = 3.14159;
const user = { name: 'John' }; // Object contents can change
user.name = 'Jane'; // Valid
// user = {}; // TypeError: Assignment to constant

// Temporal Dead Zone (TDZ)
// console.log(x); // ReferenceError
let x = 1;`,
          note: "Use const by default, let when rebinding is needed. Avoid var due to hoisting and function scope.",
        },
        {
          title: "Primitive Data Types",
          content: "Core primitive types in JavaScript",
          code: `// String - text data
const message = 'Hello';
const greeting = "World";
const combined = \`\${message}, \${greeting}!\`;

// Number - integers and floating-point
const int = 42;
const float = 3.14;
const exp = 2.998e8;
const binary = 0b1010; // Binary: 10
const octal = 0o756; // Octal: 494
const hex = 0xFF; // Hexadecimal: 255
const infinity = Infinity;
const notNumber = NaN;

// BigInt - large integers
const bigInt = 9007199254740991n;
const anotherBigInt = BigInt("9007199254740991");

// Boolean - logical type
const isTrue = true;
const isFalse = false;
const boolResult = Boolean(1);

// Undefined - uninitialized value
let undef;
const explicit = undefined;

// Null - intentional absence of value
const empty = null;

// Symbol - unique identifier
const sym1 = Symbol();
const sym2 = Symbol('description');
const sym3 = Symbol.for('shared');`,
          note: "JavaScript has 7 primitive types: string, number, bigint, boolean, undefined, null, and symbol",
        },
        {
          title: "Complex Data Types",
          content: "Reference types and collections",
          code: `// Object - key-value pairs
const person = {
  name: 'John',
  age: 30,
  address: {
    street: '123 Main St',
    city: 'Boston'
  }
};

// Array - ordered collection
const numbers = [1, 2, 3];
const mixed = ['text', 42, { key: 'value' }, [1, 2]];
const typed = new Int32Array([1, 2, 3]);

// Set - unique values
const uniqueNums = new Set([1, 2, 2, 3]);
uniqueNums.add(4);
uniqueNums.has(2); // true

// Map - key-value pairs with any key type
const userMap = new Map();
userMap.set('id', 1);
userMap.set(person, 'data');

// WeakMap & WeakSet
const weakMap = new WeakMap();
const weakSet = new WeakSet();`,
          note: "Complex types are passed by reference. Use them for collections and structured data.",
        },
        {
          title: "Type Conversion & Checking",
          content: "Converting between types and type checking",
          code: `// String Conversion
String(123);         // "123"
(123).toString();    // "123"
123 + '';           // "123"

// Number Conversion
Number('123');      // 123
parseInt('123', 10); // 123
parseFloat('12.34'); // 12.34
+'123';             // 123

// Boolean Conversion
Boolean(1);         // true
Boolean('');        // false
Boolean(0);         // false
Boolean([]);        // true
!!undefined;        // false

// Type Checking
typeof "string";    // "string"
typeof 42;         // "number"
typeof {};         // "object"
Array.isArray([]); // true
Object.prototype.toString.call({}); // "[object Object]"

// Instance Checking
const arr = [];
arr instanceof Array;  // true
arr instanceof Object; // true

// Checking for null/undefined
const val = null;
val === null;         // true
val === undefined;    // false
val == null;          // true (matches null and undefined)`,
          note: "Be careful with type coercion. Always use explicit conversion when types matter.",
        },
      ],
    },
    {
      title: "Strings & String Manipulation",
      icon: "📝",
      items: [
        {
          title: "String Creation & Properties",
          content: "Different ways to create and work with strings",
          code: `// String Creation Methods
const str1 = 'Single quotes';
const str2 = "Double quotes";
const name = 'World';
const str3 = \`Template literal with \${name}\`;

// String Properties
console.log(str1.length);        // String length
console.log(str1[0]);           // First character
console.log(str1.charAt(0));    // First character (method)
console.log(str1.charCodeAt(0)); // UTF-16 code unit
console.log(str1.codePointAt(0)); // Unicode code point

// String Concatenation
const firstName = 'John';
const lastName = 'Doe';
const fullName = firstName + ' ' + lastName;
const template = \`\${firstName} and \${lastName}\`;
const concat = firstName.concat(' ', lastName);

// Escape Sequences
const special = 'Line 1\\nLine 2\\tTabbed';
const unicode = '\\u00A9'; // Copyright symbol
const emoji = '\\u{1F600}'; // Emoji`,
          note: "Template literals are preferred for string interpolation and multiline strings.",
        },
        {
          title: "String Methods",
          content: "Common string manipulation methods",
          code: `// Case Methods
str.toLowerCase();
str.toUpperCase();
str.toLocaleLowerCase('tr'); // Locale-specific
str.toLocaleUpperCase('tr');

// Finding Substrings
str.indexOf('substring');    // First occurrence
str.lastIndexOf('substring'); // Last occurrence
str.includes('substring');    // true/false
str.startsWith('prefix');     // true/false
str.endsWith('suffix');       // true/false

// Extracting Substrings
str.slice(2, 5);     // Characters 2 to 4
str.substring(2, 5); // Characters 2 to 4
str.substr(2, 3);    // 3 characters from index 2

// Modifying Strings
str.trim();          // Remove whitespace
str.trimStart();     // Remove leading whitespace
str.trimEnd();       // Remove trailing whitespace
str.padStart(10, '.'); // Pad start to length 10
str.padEnd(10, '.');   // Pad end to length 10
str.repeat(3);         // Repeat string 3 times

// Search and Replace
str.replace('old', 'new');    // Replace first match
str.replaceAll('old', 'new'); // Replace all matches
str.match(/pattern/g);        // Array of matches
str.matchAll(/pattern/g);     // Iterator of matches
str.search(/pattern/);        // Index of match

// Splitting and Joining
str.split(',');        // Split into array
str.split('');         // Split into characters
array.join('-');       // Join array with delimiter`,
          note: "String methods don't modify the original string; they return a new string.",
        },
        {
          title: "Regular Expressions with Strings",
          content: "Pattern matching and string manipulation",
          code: `// Regular Expression Creation
const regex1 = /pattern/;
const regex2 = new RegExp('pattern');

// RegExp Flags
const regexGlobal = /pattern/g;    // Global
const regexIgnoreCase = /pattern/i; // Case-insensitive
const regexMultiline = /pattern/m;  // Multiline
const regexDotAll = /pattern/s;     // Dot matches all
const regexUnicode = /pattern/u;    // Unicode
const regexSticky = /pattern/y;     // Sticky

// Pattern Matching
const text = 'Hello, World!';
regex.test(text);         // true/false
regex.exec(text);         // Match information
text.match(regex);        // Array of matches
text.matchAll(regex);     // Iterator of matches

// Common Patterns
const patterns = {
  email: /^[\\w-]+(\\.[\\w-]+)*@[\\w-]+(\\.[\\w-]+)*\\.[a-z]{2,}$/i,
  phone: /^\\+?\\d{1,4}?[-.\\s]?\\(?\\d{1,3}?\\)?[-.\\s]?\\d{1,4}[-.\\s]?\\d{1,4}[-.\\s]?\\d{1,9}$/,
  url: /^(https?:\\/\\/)?[\\w-]+(\\.[\\w-]+)+[\\w\\-\\._~:/?#[\\]@!\\$&'\\(\\)\\*\\+,;=.]+$/
};

// Replace with Function
text.replace(/\\w+/g, match => match.toUpperCase());

// Named Capture Groups
const dateRegex = /(?<year>\\d{4})-(?<month>\\d{2})-(?<day>\\d{2})/;
const match = '2024-03-14'.match(dateRegex);
const { year, month, day } = match.groups;`,
          note: "Regular expressions are powerful but can be complex. Test thoroughly and consider readability.",
        },
      ],
    },
    {
      title: "Control Flow",
      icon: "🔄",
      items: [
        {
          title: "Conditional Statements",
          content: "Decision making in JavaScript",
          code: `// if...else Statement
if (condition) {
  // code for true
} else if (otherCondition) {
  // code for other condition
} else {
  // code for false
}

// Switch Statement
switch (value) {
  case 1:
    // code for 1
    break;
  case 2:
  case 3:
    // code for 2 or 3
    break;
  default:
    // default code
}

// Ternary Operator
const result = condition ? 'true case' : 'false case';

// Multiple Ternary
const value = a > 0 ? 'positive'
           : a < 0 ? 'negative'
           : 'zero';

// Nullish Coalescing
const value = null ?? 'default';
const nested = obj?.prop?.value ?? 'default';

// Short-Circuit Evaluation
const result1 = value || 'default';  // Falsy check
const result2 = value && 'exists';   // Truthy check`,
          note: "Choose the most readable and maintainable approach for your use case.",
        },
        {
          title: "Loops & Iteration",
          content: "Different ways to iterate in JavaScript",
          code: `// for Loop
for (let i = 0; i < 5; i++) {
  if (condition) continue; // Skip iteration
  if (otherCondition) break; // Exit loop
}

// for...of Loop (Iterables)
for (const item of array) {
  console.log(item);
}

// for...in Loop (Object Properties)
for (const key in object) {
  if (object.hasOwnProperty(key)) {
    console.log(key, object[key]);
  }
}

// while Loop
while (condition) {
  // code
}

// do...while Loop
do {
  // code
} while (condition);

// Array Methods
array.forEach((item, index, array) => {
  // code for each item
});

array.map(item => transform(item));
array.filter(item => condition(item));
array.reduce((acc, item) => acc + item, initial);

// Async Iteration
for await (const item of asyncIterable) {
  // handle async items
}

// Generator Iteration
function* generator() {
  yield 1;
  yield 2;
}
for (const value of generator()) {
  console.log(value);
}`,
          note: "Choose the appropriate loop based on what you're iterating over and what you need to do with the values.",
        },
        {
          title: "Error Handling",
          content: "Managing and handling errors",
          code: `// try...catch
try {
  // code that might throw
  throw new Error('Custom error');
} catch (error) {
  console.error(error.message);
} finally {
  // always executed
}

// Custom Error Types
class ValidationError extends Error {
  constructor(message) {
    super(message);
    this.name = 'ValidationError';
  }
}

// Async Error Handling
async function example() {
  try {
    await riskyOperation();
  } catch (error) {
    if (error instanceof NetworkError) {
      // handle network error
    } else {
      throw error; // re-throw other errors
    }
  }
}

// Promise Error Handling
promise
  .then(handleSuccess)
  .catch(handleError)
  .finally(cleanup);

// Multiple try-catch
try {
  try {
    // nested operation
  } catch (e) {
    // handle specific error
    throw e; // re-throw
  }
} catch (e) {
  // handle all errors
}`,
          note: "Always handle errors appropriately. Don't catch errors unless you can handle them meaningfully.",
        },
      ],
    },
    {
      title: "Functions & Scope",
      icon: "⚡",
      items: [
        {
          title: "Function Declarations",
          content: "Different ways to define functions",
          code: `// Function Declaration
function greet(name) {
  return \`Hello, \${name}!\`;
}

// Function Expression
const greet = function(name) {
  return \`Hello, \${name}!\`;
};

// Arrow Function
const greet = (name) => \`Hello, \${name}!\`;
const square = x => x * x; // Single parameter
const log = () => console.log('Hi'); // No parameters
const obj = () => ({ key: 'value' }); // Object return

// IIFE (Immediately Invoked Function Expression)
(function() {
  // private scope
})();

(() => {
  // arrow function IIFE
})();

// Generator Function
function* numberGenerator() {
  yield 1;
  yield 2;
  return 3;
}

// Async Function
async function fetchData() {
  return await fetch(url);
}

// Async Generator Function
async function* asyncGenerator() {
  yield await Promise.resolve(1);
}`,
          note: "Choose the appropriate function syntax based on your needs for 'this' binding, conciseness, and functionality.",
        },
        {
          title: "Function Parameters",
          content: "Working with function parameters",
          code: `// Default Parameters
function greet(name = 'Guest', greeting = 'Hello') {
  return \`\${greeting}, \${name}!\`;
}

// Rest Parameters
function sum(...numbers) {
  return numbers.reduce((a, b) => a + b, 0);
}

// Parameter Destructuring
function process({ id, name } = {}) {
  console.log(id, name);
}

// Array Parameter Destructuring
function coordinate([x, y, z = 0]) {
  console.log(x, y, z);
}

// Mixed Parameters
function mixed(a, b, ...rest) {
  console.log(a, b, rest);
}

// Function Borrowing
function method() {
  console.log(this.name);
}
method.call(obj, arg1, arg2);
method.apply(obj, [arg1, arg2]);
const bound = method.bind(obj);

// Arguments Object
function legacy() {
  console.log(arguments);
  Array.from(arguments);
}`,
          note: "Use rest parameters instead of arguments. Default parameters are evaluated at call time.",
        },
        {
          title: "Closures & Scope",
          content: "Understanding closures and scope chains",
          code: `// Basic Closure
function counter() {
  let count = 0;
  return function() {
    return ++count;
  };
}
const increment = counter();

// Module Pattern
const module = (function() {
  let private = 'secret';
  
  return {
    getPrivate: () => private,
    setPrivate: (value) => { private = value; }
  };
})();

// Currying
const curry = (fn) => {
  return function curried(...args) {
    if (args.length >= fn.length) {
      return fn.apply(this, args);
    }
    return (...more) => curried.apply(this, args.concat(more));
  };
};

// Memoization
function memoize(fn) {
  const cache = new Map();
  return (...args) => {
    const key = JSON.stringify(args);
    if (cache.has(key)) return cache.get(key);
    const result = fn.apply(this, args);
    cache.set(key, result);
    return result;
  };
}

// Lexical Scope
const x = 1;
function outer() {
  const x = 2;
  function inner() {
    console.log(x); // 2
  }
  inner();
}`,
          note: "Closures are powerful for data privacy and state management. Understanding scope is crucial for JavaScript development.",
        },
      ],
    },
    {
      title: "Objects & Classes",
      icon: "🏗️",
      items: [
        {
          title: "Object Creation & Properties",
          content: "Different ways to create and work with objects",
          code: `// Object Literal
const person = {
  name: 'John',
  age: 30,
  greet() {
    return \`Hello, \${this.name}!\`;
  }
};

// Constructor Function
function Person(name, age) {
  this.name = name;
  this.age = age;
}
const john = new Person('John', 30);

// Object.create
const proto = {
  greet() {
    return \`Hello, \${this.name}!\`;
  }
};
const jane = Object.create(proto);

// Property Descriptors
Object.defineProperty(person, 'id', {
  value: 1,
  writable: false,
  enumerable: true,
  configurable: false
});

// Getters and Setters
const account = {
  _balance: 0,
  get balance() {
    return this._balance;
  },
  set balance(value) {
    if (value >= 0) this._balance = value;
  }
};

// Property Existence
'name' in person;
person.hasOwnProperty('name');
Object.prototype.hasOwnProperty.call(person, 'name');`,
          note: "Choose the appropriate object creation pattern based on your needs for inheritance and property control.",
        },
        {
          title: "Classes & Inheritance",
          content: "Object-oriented programming with classes",
          code: `// Class Declaration
class Animal {
  // Private field
  #privateField = 'private';
  
  // Static members
  static count = 0;
  static {
    // Static initialization block
    this.count = 1;
  }
  
  constructor(name) {
    this.name = name;
    Animal.count++;
  }
  
  // Instance method
  speak() {
    return \`\${this.name} makes a sound\`;
  }
  
  // Getter
  get type() {
    return 'Animal';
  }
  
  // Static method
  static create(name) {
    return new Animal(name);
  }
}

// Inheritance
class Dog extends Animal {
  constructor(name, breed) {
    super(name);
    this.breed = breed;
  }
  
  speak() {
    return \`\${this.name} barks\`;
  }
}

// Abstract Class
class AbstractClass {
  constructor() {
    if (new.target === AbstractClass) {
      throw new Error('Cannot instantiate abstract class');
    }
  }
}

// Mixins
const speakerMixin = {
  speak() {
    console.log(\`\${this.name} speaks\`);
  }
};

Object.assign(Dog.prototype, speakerMixin);`,
          note: "Classes provide a cleaner syntax for OOP in JavaScript. Use private fields and static members for better encapsulation.",
        },
        {
          title: "Object Methods & Operations",
          content: "Common object operations and methods",
          code: `// Object Methods
Object.keys(obj);        // Array of enumerable keys
Object.values(obj);      // Array of values
Object.entries(obj);     // Array of [key, value] pairs
Object.fromEntries(entries); // Object from entries

// Cloning Objects
const shallow = { ...original };
const another = Object.assign({}, original);
const deep = JSON.parse(JSON.stringify(original));
const structuredClone = structuredClone(original);

// Object Freezing
Object.freeze(obj);    // Make immutable
Object.seal(obj);      // Prevent add/delete
Object.preventExtensions(obj); // Prevent adding

// Property Reflection
Reflect.get(obj, 'prop');
Reflect.set(obj, 'prop', value);
Reflect.has(obj, 'prop');
Reflect.deleteProperty(obj, 'prop');

// Proxy Objects
const handler = {
  get: (target, prop) => \`Property \${prop}\`,
  set: (target, prop, value) => {
    target[prop] = value;
    return true;
  }
};
const proxy = new Proxy(obj, handler);`,
          note: "Use modern object methods for cleaner code. Be careful with deep cloning of complex objects.",
        },
      ],
    },
    {
      title: "Asynchronous JavaScript",
      icon: "⏱️",
      items: [
        {
          title: "Promises",
          content: "Working with Promises",
          code: `// Creating Promises
const promise = new Promise((resolve, reject) => {
  if (success) {
    resolve(result);
  } else {
    reject(new Error('Failed'));
  }
});

// Promise Methods
promise
  .then(result => console.log(result))
  .catch(error => console.error(error))
  .finally(() => cleanup());

// Promise Static Methods
Promise.resolve(value);
Promise.reject(error);
Promise.all([promise1, promise2]);
Promise.race([promise1, promise2]);
Promise.allSettled([promise1, promise2]);
Promise.any([promise1, promise2]);

// Promise Chaining
fetchUser()
  .then(user => fetchProfile(user))
  .then(profile => updateUI(profile))
  .catch(handleError);

// Parallel Promises
const results = await Promise.all([
  fetch(url1),
  fetch(url2)
]);

// Sequential Promises
for (const url of urls) {
  await fetch(url);
}`,
          note: "Promises provide a clean way to handle asynchronous operations. Use appropriate static methods for different scenarios.",
        },
        {
          title: "Async/Await",
          content: "Modern asynchronous programming",
          code: `// Async Function
async function getData() {
  try {
    const response = await fetch(url);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(error);
  }
}

// Parallel Execution
async function getMultipleData() {
  const [data1, data2] = await Promise.all([
    fetch(url1),
    fetch(url2)
  ]);
  return [await data1.json(), await data2.json()];
}

// Async Iteration
async function* asyncGenerator() {
  for (const item of items) {
    yield await process(item);
  }
}

for await (const item of asyncGenerator()) {
  console.log(item);
}

// Error Handling
try {
  const data = await riskyOperation();
  } catch (error) {
  if (error instanceof NetworkError) {
    // handle network error
    } else {
    throw error; // re-throw
  }
}

// Async Class Methods
class DataService {
  async fetchData() {
    const response = await fetch(url);
    return response.json();
  }
}`,
          note: "Async/await provides cleaner syntax for promise-based code. Always use proper error handling.",
        },
        {
          title: "Event Loop & Timing",
          content: "Understanding JavaScript's event loop",
          code: `// Timers
setTimeout(() => {
  console.log('Delayed');
}, 1000);

setInterval(() => {
  console.log('Repeated');
}, 1000);

// Clear Timers
const timeoutId = setTimeout(callback, 1000);
clearTimeout(timeoutId);

const intervalId = setInterval(callback, 1000);
clearInterval(intervalId);

// requestAnimationFrame
function animate() {
  requestAnimationFrame(animate);
  // animation code
}

// Microtasks
Promise.resolve().then(() => {
  console.log('Microtask');
});

queueMicrotask(() => {
  console.log('Microtask');
});

// Event Loop Example
console.log('Start');
setTimeout(() => console.log('Timeout'), 0);
Promise.resolve().then(() => console.log('Promise'));
console.log('End');`,
          note: "Understanding the event loop is crucial for handling asynchronous operations correctly.",
        },
      ],
    },
    {
      title: "Modern JavaScript Features",
      icon: "✨",
      items: [
        {
          title: "ES6+ Features",
          content: "Modern JavaScript syntax and features",
          code: `// Destructuring
const { name, age } = person;
const [first, second, ...rest] = array;

// Spread Operator
const newArray = [...array, newItem];
const newObject = { ...object, newProp: value };

// Optional Chaining
const value = obj?.prop?.method?.();
const item = array?.[0];

// Nullish Coalescing
const value = null ?? defaultValue;
const nested = obj?.prop ?? defaultValue;

// Private Class Fields
class Example {
  #private = 'secret';
  static #staticPrivate = 'static secret';
  
  #privateMethod() {
    return this.#private;
  }
}

// Top-level await
const data = await fetch(url);

// Dynamic Imports
const module = await import('./module.js');

// BigInt
const bigInt = 9007199254740991n;
const result = bigInt + 1n;

// Numeric Separators
const billion = 1_000_000_000;
const bytes = 0xFF_FF_FF_FF;

// String Methods
'  text  '.trimStart();
'text'.padStart(8, '0');
'hello'.replaceAll('l', 'L');`,
          note: "Modern JavaScript features improve code readability and maintainability.",
        },
        {
          title: "Modules",
          content: "Working with ES6 modules",
          code: `// Named Exports
export const name = 'value';
export function helper() {}
export class Util {}

// Default Export
export default class Main {}

// Mixed Exports
export { name, helper };
export default main;

// Named Imports
import { name, helper } from './module';
import { name as alias } from './module';
import * as module from './module';

// Default Import
import Main from './module';

// Mixed Imports
import Main, { name, helper } from './module';

// Dynamic Imports
const module = await import('./module.js');

// Module Aggregation
export * from './other-module';
export { default } from './other-module';
export * as ns from './other-module';

// Module Attributes
import json from './data.json' assert { type: 'json' };`,
          note: "Use modules for better code organization and maintainability.",
        },
        {
          title: "Iterators & Generators",
          content: "Working with iterables and generators",
          code: `// Iterator Protocol
const iterator = {
  next() {
    return { value: 1, done: false };
  }
};

// Iterable Protocol
const iterable = {
  [Symbol.iterator]() {
    return iterator;
  }
};

// Generator Function
function* numberGenerator() {
  yield 1;
  yield 2;
  return 3;
}

// Generator with For...Of
function* range(start, end) {
  for (let i = start; i <= end; i++) {
    yield i;
  }
}

// Async Generator
async function* asyncGenerator() {
  for (const url of urls) {
    yield await fetch(url);
  }
}

// Generator Delegation
function* delegate() {
  yield* otherGenerator();
  yield* ['a', 'b', 'c'];
}`,
          note: "Generators provide a powerful way to control iteration and create data streams.",
        },
      ],
    },
  ],
};

export default javascriptCheatSheet;
